import { Moment } from 'moment-timezone'
import React, { useEffect, useRef, useState, useLayoutEffect } from 'react'

import { useCurrentTime } from 'shared/contexts'
import { scrollToDate, scrollToToday, type MeetingGroup } from 'shared/utils'

/**
 * Checks if the code is running in a browser environment (not SSR or tests).
 * @returns true if window and document are defined, false otherwise
 */
const isBrowserEnvironment = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined'

/**
 * Hook to track scroll position and restore it when meetings change.
 * Useful for maintaining scroll position when new meetings are loaded.
 *
 * @param meetings - Array of meeting groups
 * @param callback - Callback function that receives scroll data { top, height }
 * @returns Function to set scroll data
 */
export const useScrollPosition = (meetings: MeetingGroup[], callback: (scrollData: { top: number; height: number }) => void) => {
  const [scrollData, setScrollData] = useState<{ top: number; height: number } | null>(null)
  const prevMeetings = useRef(meetings)

  useLayoutEffect(() => {
    if (meetings === prevMeetings.current) return
    prevMeetings.current = meetings

    if (!scrollData) return

    callback(scrollData)
    setScrollData(null)
    prevMeetings.current = meetings
  }, [meetings, scrollData, callback])

  return setScrollData
}

/**
 * Initial scroll when meetings load or when the week preset changes.
 *
 * Week presets defer until `isListSettled` so scroll runs on the new feed, not stale data.
 *
 * @param isListSettled - When false (list refetching), week-preset scroll waits for settled data.
 * @param skipScrollToToday - Phone preset Today/Upcoming: keep scroll at top. `scrollToToday`
 *   centers the now-line and opens a white band under the utility bar above the first date.
 */
export const useInitialScroll = (
  containerRef: React.RefObject<HTMLDivElement>,
  meetings: MeetingGroup[],
  timezone: string,
  currentTime?: Moment,
  scrollToDateTarget?: string | null,
  initialScrollDate?: string | null,
  { isListSettled = true, skipScrollToToday = false } = {},
) => {
  const hookCurrentTime = useCurrentTime()
  const effectiveCurrentTime = currentTime ?? hookCurrentTime
  const effectiveCurrentTimeRef = useRef(effectiveCurrentTime)
  effectiveCurrentTimeRef.current = effectiveCurrentTime

  const scrollToDateTargetRef = useRef(scrollToDateTarget)
  scrollToDateTargetRef.current = scrollToDateTarget

  const initialScrollDateRef = useRef(initialScrollDate)
  initialScrollDateRef.current = initialScrollDate

  const skipScrollToTodayRef = useRef(skipScrollToToday)
  skipScrollToTodayRef.current = skipScrollToToday

  const hasScrolledTodayRef = useRef(false)
  const lastScrolledWeekDateRef = useRef<string | null>(null)

  useEffect(() => {
    lastScrolledWeekDateRef.current = null
  }, [initialScrollDate])

  useEffect(() => {
    if (!isListSettled) return undefined
    if (!meetings || !Array.isArray(meetings) || !meetings.length) return undefined

    const container = containerRef.current
    if (!container) return undefined

    // Date-pick scroll is owned by MeetingContainer once pivot data includes the picked day.
    if (scrollToDateTargetRef.current) return undefined

    const weekStartDate = initialScrollDateRef.current

    const timeoutId = setTimeout(() => {
      if (weekStartDate) {
        if (lastScrolledWeekDateRef.current === weekStartDate) return
        scrollToDate(containerRef, weekStartDate, true, meetings)
        lastScrolledWeekDateRef.current = weekStartDate
        return
      }
      if (hasScrolledTodayRef.current) return
      hasScrolledTodayRef.current = true
      if (skipScrollToTodayRef.current) {
        // Phone sticky Displaying N: do not scrollToToday (centers now-line) and do not
        // force scrollTop=0 — MeetingContainer aligns the first table row to feed-top.
        return
      }
      scrollToToday(containerRef, meetings, timezone, true, effectiveCurrentTimeRef.current)
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [meetings, timezone, containerRef, initialScrollDate, isListSettled])
}

/**
 * Hook to disable browser scroll restoration.
 * Prevents the browser from automatically restoring scroll position on page load.
 * Restores the original value when the component unmounts.
 */
export const useDisableScrollRestoration = () => {
  useEffect(() => {
    if (!isBrowserEnvironment()) {
      return
    }

    if ('scrollRestoration' in window.history) {
      // Store the original value to restore on unmount
      const originalValue = window.history.scrollRestoration
      window.history.scrollRestoration = 'manual'

      // Restore the original value when component unmounts
      return () => {
        window.history.scrollRestoration = originalValue
      }
    }
  }, [])
}
